import type { ReactNode } from 'react';

import type { OnboardingTravellerListControl } from '#ui/OnboardingTravellerList.js';

import { useCallback, useEffect, useRef, useState } from 'react';

import { ROUTES } from '@elara/client-core/constants';
import {
  isListedTravelCreditCard,
  textToPreferences,
  TRAVEL_CREDIT_CARD_OPTIONS,
} from '@elara/client-core/lib/preference-utils';
import { buildSupportWhatsappUrl } from '@elara/client-core/lib/support-chat';
import { cn } from '@elara/client-core/lib/utils';
import { ZodGoogleCabinClass } from '@elara/domain/models/schemas/zod-types';

import { useNavigate, useOnboardingState, useUserProfileState } from '#states/hooks.js';
import { Checkbox } from '#ui/checkbox.js';
import { useTypingEffect } from '#ui/hooks/useTypingEffect.js';
import { ElaraIcon } from '#ui/icons/elara-icon.js';
import { EmptyChatIllustration } from '#ui/illustrations/empty-chat.js';
import { AppImage, Button, Card, CardContent, Input, Loader, Textarea } from '#ui/index.js';
import { OnboardingTravellerList } from '#ui/OnboardingTravellerList.js';

const CABIN_CLASS_OPTIONS = ZodGoogleCabinClass.options;

/** Indices for special screens */
const WELCOME_INDEX = 0;
const INTRO_INDEX = 1;
const FIRST_QUESTION_INDEX = 2;
const TRAVEL_PREFS_COUNT = 6; // based, wishlist, airports, domestic class, intl class, hotels
const FAMILY_INDEX = FIRST_QUESTION_INDEX + TRAVEL_PREFS_COUNT; // 8
const KEY_DATES_INDEX = FAMILY_INDEX + 1; // 9
const CREDIT_CARDS_INDEX = KEY_DATES_INDEX + 1; // 10
const OTHER_PREFS_INDEX = CREDIT_CARDS_INDEX + 1; // 11
const CLOSING_INDEX = OTHER_PREFS_INDEX + 1; // 12
const TOTAL_SLIDES = CLOSING_INDEX + 1; // 13

type Phase = 'visible' | 'exiting' | 'entering';

export function ConversationalOnboarding() {
  const onboardingState = useOnboardingState();
  const navigate = useNavigate();

  const userProfile = useUserProfileState().profile;
  const firstName = userProfile?.travelerProfile?.firstName;

  const travelPrefsInitial = onboardingState.getTravelPreferencesInitial();
  const familyInitial = onboardingState.getFamilyInitial();

  // Form state for travel preferences
  const [city, setCity] = useState((userProfile?.address as { city?: string } | undefined)?.city || '');
  const [wishlistDestinations, setWishlistDestinations] = useState(travelPrefsInitial.wishlistDestinations);
  const [preferredAirports, setPreferredAirports] = useState(travelPrefsInitial.preferredAirports);
  const [domesticFlightClass, setDomesticFlightClass] = useState(travelPrefsInitial.domesticFlightClass);
  const [internationalFlightClass, setInternationalFlightClass] = useState(travelPrefsInitial.internationalFlightClass);
  const [hotelPreferences, setHotelPreferences] = useState(travelPrefsInitial.hotelPreferences);
  const [keyDatesText, setKeyDatesText] = useState(travelPrefsInitial.keyDatesText);
  const [travelCreditCards, setTravelCreditCards] = useState<string[]>(() => {
    const initial = travelPrefsInitial.travelCreditCards;
    if (!initial) return [];
    return initial.split('\n').filter((v) => isListedTravelCreditCard(v));
  });
  const [travelCreditCardsOther, setTravelCreditCardsOther] = useState(() => {
    const initial = travelPrefsInitial.travelCreditCards;
    if (!initial) return '';
    return initial
      .split('\n')
      .filter((v) => v && !isListedTravelCreditCard(v))
      .join('\n');
  });
  const [preferences, setPreferences] = useState(travelPrefsInitial.preferences);

  // Family state
  const travellerListRef = useRef<OnboardingTravellerListControl>(null);
  const [savedTravellers, setSavedTravellers] = useState(familyInitial.travellers);

  // Flow state
  // Resumes at the step reached before a refresh (answers are saved as each step is left)
  const [currentIndex, setCurrentIndex] = useState(() =>
    Math.min(onboardingState.savedStep ?? WELCOME_INDEX, CLOSING_INDEX),
  );
  const [phase, setPhase] = useState<Phase>('visible');
  const [isForward, setIsForward] = useState(true);
  const [validationError, setValidationError] = useState<string | undefined>();
  const seenSlides = useRef(new Set<number>());
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onboardingState.rememberStep(currentIndex);
  }, [onboardingState, currentIndex]);

  // Leaving the welcome screen starts preparing the demo trip; a resumed onboarding has already left it.
  useEffect(() => {
    if (currentIndex > WELCOME_INDEX) onboardingState.prepareDemoTrip();
    // Once, on mount: later steps are reached through the welcome screen's own call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Returns an error message if the current question is required but empty, undefined otherwise. */
  const validateCurrentQuestion = useCallback((): string | undefined => {
    const OPTIONAL_INDICES = new Set([
      WELCOME_INDEX,
      INTRO_INDEX,
      FAMILY_INDEX,
      KEY_DATES_INDEX,
      CREDIT_CARDS_INDEX,
      OTHER_PREFS_INDEX,
      CLOSING_INDEX,
    ]);
    if (OPTIONAL_INDICES.has(currentIndex)) return;

    switch (currentIndex) {
      case 2:
        return city.trim() ? undefined : 'Please enter your city';
      case 3:
        return wishlistDestinations.trim() ? undefined : 'Please enter at least one destination';
      case 4:
        return preferredAirports.trim() ? undefined : 'Please enter at least one airport';
      case 5:
        return domesticFlightClass.length > 0 ? undefined : 'Please select at least one option';
      case 6:
        return internationalFlightClass.length > 0 ? undefined : 'Please select at least one option';
      case 7:
        return hotelPreferences.trim() ? undefined : 'Please enter your hotel preferences';
      default:
        return;
    }
  }, [
    currentIndex,
    city,
    wishlistDestinations,
    preferredAirports,
    domesticFlightClass,
    internationalFlightClass,
    hotelPreferences,
  ]);

  const isWelcome = currentIndex === WELCOME_INDEX;
  const isQuestion = !isWelcome;

  const transitionTo = useCallback(
    (nextIndex: number, forward = true) => {
      // Mark the current slide as seen before leaving it
      seenSlides.current.add(currentIndex);
      setIsForward(forward);
      setPhase('exiting');
      setTimeout(() => {
        setCurrentIndex(nextIndex);
        setPhase('entering');
        containerRef.current?.scrollTo({ top: 0 });
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setPhase('visible');
          });
        });
      }, 200);
    },
    [currentIndex],
  );

  /**
   * Saves the step being left, so answers survive a refresh. Each step saves only its own answer:
   * preference steps per entry (deleting a line removes it), travel settings over the stored profile,
   * and companions one at a time by id. Returns false (with the error shown) if the save failed.
   */
  const saveStep = useCallback(
    async (index: number): Promise<boolean> => {
      switch (index) {
        case FIRST_QUESTION_INDEX: {
          if (!city.trim()) return true;
          const address = { ...(userProfile?.address as Record<string, unknown> | undefined), city };
          return onboardingState.saveProfile({ address });
        }
        case FIRST_QUESTION_INDEX + 1:
          return onboardingState.savePreferenceLines({ text: wishlistDestinations, category: 'destination' });
        case FIRST_QUESTION_INDEX + 2:
          return onboardingState.savePreferenceLines({ text: preferredAirports, category: 'airport' });
        case FIRST_QUESTION_INDEX + 3:
          return onboardingState.saveTravelerProfileFields({
            domesticFlightClass: ZodGoogleCabinClass.array().parse(domesticFlightClass),
          });
        case FIRST_QUESTION_INDEX + 4:
          return onboardingState.saveTravelerProfileFields({
            internationalFlightClass: ZodGoogleCabinClass.array().parse(internationalFlightClass),
          });
        case FIRST_QUESTION_INDEX + 5:
          return onboardingState.savePreferenceLines({ text: hotelPreferences, category: 'hotel' });
        case FAMILY_INDEX: {
          const list = travellerListRef.current;
          const saved = await onboardingState.saveTravellers(list?.getTravellers() ?? savedTravellers);
          if (!saved) return false;
          setSavedTravellers(saved);
          return true;
        }
        case KEY_DATES_INDEX:
          return onboardingState.savePreferenceLines({ text: keyDatesText, category: 'key_date' });
        case CREDIT_CARDS_INDEX:
          return onboardingState.saveTravelerProfileFields({
            travelCreditCards: textToPreferences(
              [...travelCreditCards, ...travelCreditCardsOther.split('\n').filter(Boolean)].join('\n'),
            ),
          });
        case OTHER_PREFS_INDEX:
          return onboardingState.savePreferenceLines({
            text: preferences,
            category: 'general',
            shownCategories: ['dining', 'dietary', 'general'],
          });
        default:
          return true;
      }
    },
    [
      city,
      wishlistDestinations,
      preferredAirports,
      domesticFlightClass,
      internationalFlightClass,
      hotelPreferences,
      keyDatesText,
      travelCreditCards,
      travelCreditCardsOther,
      preferences,
      savedTravellers,
      onboardingState,
      userProfile?.address,
    ],
  );

  const handleContinue = useCallback(async () => {
    // Validate required questions
    const error = validateCurrentQuestion();
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(undefined);

    // Validate travellers before advancing past the family step
    if (currentIndex === FAMILY_INDEX) {
      const list = travellerListRef.current;
      if (list && !list.validate()) return;
    }

    // Every answer is already saved; the closing step completes onboarding
    if (currentIndex === CLOSING_INDEX) {
      const tripId = await onboardingState.completeOnboarding();
      if (tripId) {
        navigate.navigate(`/trip/${tripId}`, { replace: true });
      }
      return;
    }

    if (!(await saveStep(currentIndex))) return;
    transitionTo(currentIndex + 1);
  }, [currentIndex, transitionTo, saveStep, onboardingState, navigate, validateCurrentQuestion]);

  const handleBack = useCallback(async () => {
    if (currentIndex === FAMILY_INDEX) {
      const list = travellerListRef.current;
      if (list && !list.validate()) return;
    }
    // Keeps what was typed on this step, though going back doesn't require it to be complete
    if (!(await saveStep(currentIndex))) return;
    if (currentIndex > WELCOME_INDEX) {
      transitionTo(currentIndex - 1, false);
    }
  }, [currentIndex, transitionTo, saveStep]);

  const handleSkipTour = useCallback(async () => {
    await onboardingState.completeOnboarding();
    navigate.navigate(ROUTES.TRIP_NEW, { replace: true });
  }, [onboardingState, navigate]);

  const transitionClasses =
    phase === 'visible'
      ? 'opacity-100 translate-y-0'
      : phase === 'exiting'
        ? 'opacity-0 translate-y-2'
        : 'opacity-0 translate-y-2';

  // Progress across all slides (welcome = 1 step, then questions through closing)
  const progress = (currentIndex + 1) / TOTAL_SLIDES;

  return (
    <div ref={containerRef} className="h-full flex flex-col overflow-auto">
      {/* Progress bar */}
      <div className="sticky top-0 z-10 h-1 bg-muted">
        <div
          className="h-full bg-accent transition-all duration-300 ease-out"
          style={{ width: currentIndex === CLOSING_INDEX ? '100%' : `${progress * 100}%` }}
        />
      </div>

      <div className="flex-1 flex items-center justify-center">
        <div
          className={`max-w-2xl mx-auto w-full px-4 py-8 md:py-16 transition-all duration-200 ease-in-out ${transitionClasses}`}
        >
          {isWelcome && (
            <WelcomeScreen
              onContinue={() => {
                onboardingState.prepareDemoTrip();
                transitionTo(INTRO_INDEX);
              }}
            />
          )}

          {isQuestion && (
            <QuestionScreen
              key={currentIndex}
              index={currentIndex}
              animate={isForward && !seenSlides.current.has(currentIndex)}
              city={city}
              setCity={setCity}
              wishlistDestinations={wishlistDestinations}
              setWishlistDestinations={setWishlistDestinations}
              preferredAirports={preferredAirports}
              setPreferredAirports={setPreferredAirports}
              domesticFlightClass={domesticFlightClass}
              setDomesticFlightClass={setDomesticFlightClass}
              internationalFlightClass={internationalFlightClass}
              setInternationalFlightClass={setInternationalFlightClass}
              hotelPreferences={hotelPreferences}
              setHotelPreferences={setHotelPreferences}
              keyDatesText={keyDatesText}
              setKeyDatesText={setKeyDatesText}
              travelCreditCards={travelCreditCards}
              setTravelCreditCards={setTravelCreditCards}
              travelCreditCardsOther={travelCreditCardsOther}
              setTravelCreditCardsOther={setTravelCreditCardsOther}
              preferences={preferences}
              setPreferences={setPreferences}
              familyInitial={familyInitial}
              savedTravellers={savedTravellers}
              travellerListRef={travellerListRef}
              onContinue={handleContinue}
              onBack={handleBack}
              onSkip={
                [FAMILY_INDEX, KEY_DATES_INDEX, CREDIT_CARDS_INDEX].includes(currentIndex)
                  ? () => transitionTo(currentIndex + 1)
                  : undefined
              }
              onSkipTour={handleSkipTour}
              isSaving={onboardingState.isSaving}
              firstName={firstName}
              validationError={validationError}
              isNextDisabled={!!validateCurrentQuestion()}
            />
          )}

          {onboardingState.error && (
            <div className="bg-destructive/10 text-destructive px-4 py-2 rounded-md text-sm mt-4">
              {onboardingState.error.message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WelcomeScreen({ onContinue }: { onContinue: () => void }) {
  return (
    <div className="flex flex-col gap-6 items-center text-center">
      <div className="flex flex-col md:items-center gap-2 md:my-4">
        <h1 className="text-2xl xl:text-3xl font-display font-light">Meet your travel team</h1>
        <div className="text-muted-foreground md:text-center text-pretty">
          We'll start with setting up your profile so Elara can personalize your trip discovery.
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 w-full">
        <Card className="w-full shadow-sm p-6">
          <CardContent className="flex flex-col">
            <div className="font-medium">Elara</div>

            <div className="flex relative items-center justify-center h-52">
              <Loader
                thinking={true}
                width={102}
                height={102}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              />
              <EmptyChatIllustration width={260} height={184} />
            </div>

            <p className="text-sm text-left">
              <span className="font-semibold">Elara</span> is your personal travel assistant — available 24/7, Elara
              learns your preferences and family’s travel style to craft trips you'll love.
            </p>
          </CardContent>
        </Card>

        <Card className="w-full shadow-sm p-6">
          <CardContent className="flex flex-col">
            <div className="font-medium">Shannon</div>

            <div className="flex relative items-center justify-center h-52">
              <AppImage
                src="/team/shannon.jpg"
                alt="Shannon"
                width="160"
                height="160"
                style={{ borderRadius: '55% 45% 55% 45% / 43% 48% 52% 57% ' }}
              />
            </div>

            <p className="text-sm text-left">
              <span className="font-semibold">Shannon Culler</span> is your dedicated Travel Advisor. She’s on hand via{' '}
              <a href={buildSupportWhatsappUrl()} target="_blank" rel="noopener noreferrer" className="link">
                WhatsApp
              </a>{' '}
              to help gut check an itinerary, give advice, and help finalize bookings.
            </p>
          </CardContent>
        </Card>
      </div>

      <Button size="lg" className="mt-4 px-8" onClick={onContinue}>
        Set up your profile
        {/* <MaterialIcon icon="arrow_forward" size={IconSizes.xs} /> */}
      </Button>

      <p className="text-xs text-muted-foreground">
        You can update your travel profile at any time from account settings.
      </p>
    </div>
  );
}

interface QuestionScreenProps {
  index: number;
  /** Whether to animate the typing effect (true = forward, false = back/instant) */
  animate: boolean;
  city: string;
  setCity: (v: string) => void;
  wishlistDestinations: string;
  setWishlistDestinations: (v: string) => void;
  preferredAirports: string;
  setPreferredAirports: (v: string) => void;
  domesticFlightClass: string[];
  setDomesticFlightClass: (v: string[]) => void;
  internationalFlightClass: string[];
  setInternationalFlightClass: (v: string[]) => void;
  hotelPreferences: string;
  setHotelPreferences: (v: string) => void;
  keyDatesText: string;
  setKeyDatesText: (v: string) => void;
  travelCreditCards: string[];
  setTravelCreditCards: (v: string[]) => void;
  travelCreditCardsOther: string;
  setTravelCreditCardsOther: (v: string) => void;
  preferences: string;
  setPreferences: (v: string) => void;
  familyInitial: { travellers: any[] };
  savedTravellers: any[];
  travellerListRef: React.RefObject<OnboardingTravellerListControl | null>;
  onContinue: () => void;
  onBack: () => void;
  onSkip?: () => void;
  onSkipTour?: () => void;
  isSaving: boolean;
  firstName?: string;
  validationError?: string;
  isNextDisabled?: boolean;
}

function QuestionScreen(props: QuestionScreenProps) {
  const { index, animate, onContinue, onBack, onSkip, onSkipTour, isSaving } = props;
  const onboardingState = useOnboardingState();

  const question = getQuestion(index, props);
  const typing = useTypingEffect(question.prompt, animate, { speed: 60 });
  const displayedText = animate ? typing.displayedText : question.prompt;
  const isTyping = animate && typing.isTyping;
  const showControls = animate ? typing.displayedText === question.prompt : true;

  // Cmd+Enter / Ctrl+Enter to submit
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && showControls && !isSaving) {
        e.preventDefault();
        onContinue();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onContinue, showControls, isSaving]);

  return (
    <div className="flex flex-col gap-6">
      {/* Elara prompt */}
      <div className="flex flex-col md:flex-row items-start gap-3">
        <ElaraIcon size="lg" className="text-accent shrink-0 hidden -mt-0.75 md:block" />
        <div className="flex flex-col gap-1.5">
          <h2 className="text-lg whitespace-pre-line">
            {displayedText}
            {isTyping && (
              <span className="inline-block w-0.5 h-[1em] bg-foreground/60 align-text-bottom ml-px animate-pulse" />
            )}
          </h2>
        </div>
      </div>

      {/* Input area + navigation — height expands smoothly, then content fades in */}
      <div
        className="grid transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: showControls ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div
            className={cn(
              'flex flex-col gap-6 transition-opacity duration-300',
              showControls ? 'opacity-100 delay-300' : 'opacity-0',
            )}
          >
            {question.content && (
              <div className="md:pl-11">
                {question.content}
                {question.description && <p className="text-muted-foreground text-sm mt-2">{question.description}</p>}

                {props.validationError && <p className="text-destructive text-sm mt-2">{props.validationError}</p>}
              </div>
            )}

            <div className={cn('flex md:pl-11', index !== INTRO_INDEX && index !== CLOSING_INDEX && 'justify-between')}>
              <div>
                {index !== INTRO_INDEX && index !== CLOSING_INDEX && (
                  <Button variant="outline" className="bg-transparent" onClick={onBack} disabled={isSaving}>
                    Back
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-3">
                {onSkip && (
                  <Button variant="ghost" onClick={onSkip} disabled={isSaving}>
                    Skip
                  </Button>
                )}
                <Button
                  onClick={onContinue}
                  disabled={props.isNextDisabled}
                  isBusy={isSaving || onboardingState.isCompleting}
                  tooltip={
                    index === INTRO_INDEX ? `${navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'} + ⏎` : undefined
                  }
                >
                  {index === CLOSING_INDEX ? 'Take the tour' : index === INTRO_INDEX ? 'Let’s do it' : 'Next'}
                </Button>
              </div>
              {index === CLOSING_INDEX && onSkipTour && (
                <Button
                  variant="ghost"
                  onClick={onSkipTour}
                  className="ml-2"
                  isBusy={isSaving || onboardingState.isCompleting}
                >
                  Skip the tour
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function getQuestion(
  index: number,
  props: QuestionScreenProps,
  firstName?: string,
): { prompt: string; description?: string; content: ReactNode } {
  switch (index) {
    case INTRO_INDEX:
      return {
        prompt: `${props.firstName ? `Nice to meet you ${props.firstName}!` : 'Nice to meet you!'} I’m Elara, your personal travel assistant.\n\nI won’t recommend a hotel or destination just because it ranks well online. My recommendations come from a curated collection built by our in-house travel experts matched to how you and your family actually like to travel.\n\nTo get that right, I’d love to learn a little about you. It just takes a couple of minutes.`,
        content: null,
      };
    case 2:
      return {
        prompt: 'First off, where are you based?',
        content: (
          <Input
            value={props.city}
            onChange={(e) => props.setCity(e.target.value)}
            placeholder="e.g. New York, London, Sydney"
            autoFocus
            className="sm:text-base"
          />
        ),
      };
    case 3:
      return {
        prompt: 'What destinations are on your travel dream list?',
        content: (
          <Textarea
            value={props.wishlistDestinations}
            onChange={(e) => props.setWishlistDestinations(e.target.value)}
            rows={3}
            autoResize
            autoFocus
            className="sm:text-base"
          />
        ),
      };
    case 4:
      return {
        prompt: 'Which airports are most convenient for you to fly from?',
        content: (
          <Textarea
            value={props.preferredAirports}
            onChange={(e) => props.setPreferredAirports(e.target.value)}
            rows={3}
            autoResize
            autoFocus
            className="sm:text-base"
          />
        ),
      };
    case 5:
      return {
        prompt: 'When you’re flying domestically, what cabin class do you usually go for?',
        content: <CabinClassCheckboxes value={props.domesticFlightClass} onChange={props.setDomesticFlightClass} />,
      };
    case 6:
      return {
        prompt: 'And for international flights?',
        content: (
          <CabinClassCheckboxes value={props.internationalFlightClass} onChange={props.setInternationalFlightClass} />
        ),
      };
    case 7:
      return {
        prompt:
          'Let’s talk about hotels. Do you have any preferences? Do you lean more towards boutique hotels or big-name brands? Modern interiors or more Grand Dame, palace hotels?',
        content: (
          <Textarea
            value={props.hotelPreferences}
            onChange={(e) => props.setHotelPreferences(e.target.value)}
            rows={3}
            autoResize
            autoFocus
            className="sm:text-base"
          />
        ),
      };
    case FAMILY_INDEX:
      return {
        prompt:
          'Who do you usually travel with? Adding your family and friends helps me plan trips for your whole group.',
        content: (
          <OnboardingTravellerList
            initial={props.savedTravellers.length > 0 ? props.savedTravellers : props.familyInitial.travellers}
            controlRef={props.travellerListRef}
          />
        ),
      };
    case KEY_DATES_INDEX:
      return {
        prompt: 'Are there any dates I should look out for? School holidays, anniversaries, that kind of thing?',
        content: (
          <Textarea
            value={props.keyDatesText}
            onChange={(e) => props.setKeyDatesText(e.target.value)}
            rows={3}
            autoResize
            autoFocus
            className="sm:text-base"
          />
        ),
      };
    case CREDIT_CARDS_INDEX:
      return {
        prompt:
          'Do you have any travel credit cards? Some cards unlock special hotel rates and perks that I can watch for.',
        content: (
          <CreditCardCheckboxes
            value={props.travelCreditCards}
            onChange={props.setTravelCreditCards}
            otherValue={props.travelCreditCardsOther}
            onOtherChange={props.setTravelCreditCardsOther}
          />
        ),
      };
    case OTHER_PREFS_INDEX:
      return {
        prompt: 'Last one. Anything else I should keep in mind when making recommendations?',
        content: (
          <Textarea
            value={props.preferences}
            onChange={(e) => props.setPreferences(e.target.value)}
            rows={3}
            autoResize
            autoFocus
            placeholder="Mobility needs, dietary restrictions, any other preferences you have."
            className="sm:text-base"
          />
        ),
      };
    case CLOSING_INDEX:
      return {
        prompt:
          'You’re all set! I’ve put together a demo trip so you can see how everything works. Ready to take a look?',
        content: null,
      };
    default:
      return { prompt: '', content: null };
  }
}

/** Multiple-choice cabin class selector */
function CabinClassCheckboxes({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const toggle = (optionValue: string) => {
    const next = value.includes(optionValue) ? value.filter((v) => v !== optionValue) : [...value, optionValue];
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-3">
      {CABIN_CLASS_OPTIONS.map((option) => {
        const isChecked = value.includes(option);
        return (
          <label
            key={option}
            className={cn(
              'flex items-center gap-3 cursor-pointer rounded-sm border px-4 py-3 bg-background',
              isChecked ? 'border-accent' : 'border-input',
            )}
          >
            <Checkbox
              checked={isChecked}
              onCheckedChange={() => toggle(option)}
              className={cn(isChecked && 'text-accent border-accent data-[state=checked]:bg-accent')}
            />
            <span>{option}</span>
          </label>
        );
      })}
    </div>
  );
}

/** Multiple-choice credit card selector with "Other" free-text field */
function CreditCardCheckboxes({
  value,
  onChange,
  otherValue,
  onOtherChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  otherValue: string;
  onOtherChange: (v: string) => void;
}) {
  const toggle = (card: string) => {
    const next = value.includes(card) ? value.filter((v) => v !== card) : [...value, card];
    onChange(next);
  };

  const [showOther, setShowOther] = useState(otherValue.length > 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-2">
        {TRAVEL_CREDIT_CARD_OPTIONS.map((card) => {
          const isChecked = value.includes(card);
          return (
            <label
              key={card}
              className={cn(
                'flex items-center gap-3 cursor-pointer rounded-sm border px-4 py-3 bg-background',
                isChecked ? 'border-accent' : 'border-input',
              )}
            >
              <Checkbox
                checked={isChecked}
                onCheckedChange={() => toggle(card)}
                className={cn(isChecked && 'text-accent border-accent data-[state=checked]:bg-accent')}
              />
              <span className="text-sm">{card}</span>
            </label>
          );
        })}
      </div>
      <label
        className={cn(
          'flex items-center gap-3 cursor-pointer rounded-sm border px-4 py-3 bg-background',
          showOther ? 'border-accent' : 'border-input',
        )}
      >
        <Checkbox
          checked={showOther}
          onCheckedChange={(checked) => {
            setShowOther(!!checked);
            if (!checked) onOtherChange('');
          }}
          className={cn(showOther && 'text-accent border-accent data-[state=checked]:bg-accent')}
        />
        <span className="text-sm">Other</span>
      </label>
      {showOther && (
        <Input
          value={otherValue}
          onChange={(e) => onOtherChange(e.target.value)}
          placeholder="e.g. Capital One Venture X, Delta SkyMiles Reserve"
          autoFocus
          className="sm:text-base"
        />
      )}
    </div>
  );
}
